import React, { useEffect, useState } from 'react';
import { Navigate, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { useAccess } from '../app/providers/AccessProvider.jsx';
import {
  completePasswordRecovery,
  loadProfile,
  removeProfileAvatar,
  sendPasswordReset,
  signIn,
  signOut,
  signUp,
  updateProfile,
  uploadProfileAvatar,
} from '../services/account/accountService.js';
import { cancelMembership } from '../services/commerce/commerceService.js';
import {
  loadMyPortalAccess,
  portalAccessMap,
  portalIsUsable,
} from '../services/account/portalAccessService.js';
import '../styles/account.css';
import { safeReturnPath } from '../services/commerce/offerPresentation.js';
import { MembershipPlans, DeviceGuide } from '../components/commerce/MembershipPlans.jsx';

function AuthShell({ eyebrow = 'WatchVIM', title, subtitle, children, backTo = '', backLabel = 'Back' }) {
  return (
    <section className="account-page auth-page">
      <div className="account-card auth-card">
        {backTo ? <NavLink className="account-back-link" to={backTo}><span aria-hidden="true">←</span>{backLabel}</NavLink> : null}
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        {subtitle ? <p className="account-muted">{subtitle}</p> : null}
        {children}
      </div>
    </section>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="account-field">
      <span>{label}</span>
      <input {...props} />
    </label>
  );
}

function FormMessage({ error, success }) {
  if (error) return <p className="account-message error" role="alert">{error}</p>;
  if (success) return <p className="account-message success" role="status">{success}</p>;
  return null;
}

function avatarFromUser(user) {
  return String(user?.user_metadata?.avatar_url || user?.user_metadata?.avatarUrl || '').trim();
}

function avatarInitials(name, email) {
  const source = String(name || '').trim();
  if (source) {
    return source.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  }
  return String(email || 'W').trim().slice(0, 1).toUpperCase() || 'W';
}

function ProfileAvatar({ src, name, email, size = 'large' }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const label = name || email || 'WatchVIM profile';
  return (
    <div className={`profile-avatar profile-avatar-${size}`} aria-label={`${label} profile image`}>
      {src && !failed ? (
        <img src={src} alt="" loading="eager" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{avatarInitials(name, email)}</span>
      )}
    </div>
  );
}

function ProfileBackLink() {
  return <NavLink className="account-back-link profile-back-link" to="/profile"><span aria-hidden="true">←</span>Back to Profile</NavLink>;
}

export function LoginPage() {
  const { ready, user } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const returnTo = safeReturnPath(params.get('return'));
  const passwordReset = params.get('password_reset') === '1';

  if (ready && user) return <Navigate to={returnTo} replace />;

  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      await signIn(email, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err?.message || 'Unable to log in.');
    } finally { setBusy(false); }
  }

  return (
    <AuthShell title="Welcome back" subtitle="Log in to WatchVIM to manage membership, rentals, and Continue Watching.">
      <form className="account-form" onSubmit={submit}>
        <Field label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <FormMessage error={error} success={passwordReset ? 'Your password was updated. Log in with your new password.' : ''} />
        <button className="account-primary" type="submit" disabled={busy}>{busy ? 'Logging in…' : 'Log In'}</button>
      </form>
      <div className="account-links"><NavLink to="/forgot-password">Forgot password?</NavLink><NavLink to={`/signup?return=${encodeURIComponent(returnTo)}`}>Create account</NavLink></div>
    </AuthShell>
  );
}

export function SignupPage() {
  const { ready, user } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = safeReturnPath(params.get('return'));
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (ready && user) return <Navigate to={returnTo} replace />;

  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError(''); setSuccess('');
    try {
      const result = await signUp({ email, password, fullName, returnTo });
      if (result?.session) navigate(returnTo, { replace: true });
      else setSuccess('Check your email to confirm your WatchVIM account, then return to log in.');
    } catch (err) { setError(err?.message || 'Unable to create your account.'); }
    finally { setBusy(false); }
  }

  return (
    <AuthShell title="Create your WatchVIM account" subtitle="One account for streaming, membership, rentals, and viewing progress.">
      <form className="account-form" onSubmit={submit}>
        <Field label="Name" type="text" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <Field label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label="Password" type="password" autoComplete="new-password" minLength="8" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <FormMessage error={error} success={success} />
        <button className="account-primary" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create Account'}</button>
      </form>
      <div className="account-links"><NavLink to={`/login?return=${encodeURIComponent(returnTo)}`}>Already have an account? Log in</NavLink></div>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function submit(event) {
    event.preventDefault(); setBusy(true); setError(''); setSuccess('');
    try { await sendPasswordReset(email); setSuccess('Password reset instructions were sent if that email belongs to a WatchVIM account.'); }
    catch (err) { setError(err?.message || 'Unable to send password reset instructions.'); }
    finally { setBusy(false); }
  }

  return (
    <AuthShell title="Reset your password" subtitle="Enter the email connected to your WatchVIM account.">
      <form className="account-form" onSubmit={submit}>
        <Field label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <FormMessage error={error} success={success} />
        <button className="account-primary" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send Reset Link'}</button>
      </form>
      <div className="account-links"><NavLink to="/login">Back to login</NavLink></div>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const { ready, user, recoveryMode } = useSession();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event) {
    event.preventDefault(); setError('');
    if (password !== confirmPassword) {
      setError('The passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await completePasswordRecovery(password);
      navigate('/login?password_reset=1', { replace: true });
    } catch (err) { setError(err?.message || 'Unable to update your password.'); }
    finally { setBusy(false); }
  }

  if (!ready) return <AuthShell title="Checking your reset link" subtitle="Securely verifying your password-recovery session…" />;
  if (!user || !recoveryMode) {
    return <AuthShell title="Open your reset link" subtitle="Use the latest password-reset link sent to your email. Expired or previously used links cannot change your password."><NavLink className="account-primary inline" to="/forgot-password">Send a New Reset Link</NavLink></AuthShell>;
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Your reset link has been verified. Choose a new password for your WatchVIM account.">
      <form className="account-form" onSubmit={submit}>
        <Field label="New password" type="password" autoComplete="new-password" minLength="8" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Field label="Confirm new password" type="password" autoComplete="new-password" minLength="8" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
        <FormMessage error={error} />
        <button className="account-primary" type="submit" disabled={busy}>{busy ? 'Updating…' : 'Update Password'}</button>
      </form>
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnPath(params.get('return'));
  return <AuthShell title="Verify your email" subtitle="Your email confirmation is being handled securely by WatchVIM. Once verified, log in to continue."><NavLink className="account-primary inline" to={`/login?return=${encodeURIComponent(returnTo)}`}>Go to Login</NavLink></AuthShell>;
}

function RequireAccount({ children }) {
  const { ready, user } = useSession();
  const location = useLocation();
  if (!ready) return <section className="account-page"><p>Loading account…</p></section>;
  if (!user) return <Navigate to={`/login?return=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

export function ProfilePage() {
  const { user } = useSession();
  const access = useAccess();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [workspaceRows, setWorkspaceRows] = useState([]);
  const [workspaceError, setWorkspaceError] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    loadProfile(user.id).then(setProfile).catch((err) => setError(err?.message || 'Unable to load profile.'));
    loadMyPortalAccess(user.id)
      .then(setWorkspaceRows)
      .catch((err) => setWorkspaceError(err?.message || 'Unable to load workspace access.'));
  }, [user?.id]);

  async function logout() {
    await signOut();
    navigate('/home', { replace: true });
  }

  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email || 'Your WatchVIM Profile';
  const avatarUrl = profile?.avatar_url || avatarFromUser(user);
  const workspaces = portalAccessMap(workspaceRows);
  const vimPayWorkspace = workspaces.vim_pay || null;
  const filmmakerWorkspace = workspaces.filmmaker || null;
  const hasAdditionalWorkspaces = Boolean(vimPayWorkspace || filmmakerWorkspace);

  return (
    <RequireAccount>
      <section className="account-page profile-page">
        <div className="profile-overview">
          <ProfileAvatar src={avatarUrl} name={displayName} email={user?.email} />
          <div className="account-heading profile-account-heading"><span className="eyebrow">Account</span><h1>{displayName}</h1><p>{user?.email}</p><NavLink className="profile-photo-link" to="/profile/edit">Edit profile &amp; photo</NavLink></div>
        </div>
        <FormMessage error={error || workspaceError || (access.error ? 'Membership verification is temporarily unavailable.' : '')} />
        <div className="account-dashboard-grid">
          <article className="account-card"><h2>Membership</h2><strong>{access.subscription.active ? 'Active' : 'No active membership'}</strong><p>{access.subscription.plan || 'WatchVIM streaming'}</p><NavLink to="/profile/membership">Manage Membership</NavLink></article>
          <article className="account-card"><h2>Rentals</h2><strong>{access.tvod.items.length}</strong><p>Currently active rental entitlement{access.tvod.items.length === 1 ? '' : 's'}.</p><NavLink to="/profile/rentals">View Rentals</NavLink></article>
          <article className="account-card"><h2>Continue Watching</h2><p>Resume movies and episodes from your WatchVIM progress.</p><NavLink to="/continue-watching">Continue Watching</NavLink></article>
          <article className="account-card"><h2>Profile</h2><p>Update the name and avatar connected to this account.</p><NavLink to="/profile/edit">Edit Profile</NavLink></article>
        </div>
        {hasAdditionalWorkspaces ? (
          <section className="account-workspace-section" aria-label="Account workspaces">
            <div className="account-workspace-heading">
              <span className="eyebrow">One Login · Separate Access</span>
              <h2>Your WatchVIM workspaces</h2>
              <p>The email above is your single secure identity. Streaming, VIM Pay, and Filmmaker Distribution permissions stay separate.</p>
            </div>
            <div className="account-dashboard-grid">
              <article className="account-card">
                <h2>WatchVIM Streaming</h2>
                <strong>Viewer Access</strong>
                <p>Membership, rentals, viewing history, and streaming entitlements are managed independently from your work portals.</p>
                <NavLink to="/home">Open WatchVIM</NavLink>
              </article>
              {vimPayWorkspace ? (
                <article className="account-card">
                  <h2>VIM Pay</h2>
                  <strong>{String(vimPayWorkspace.status || 'invited').replace(/_/g, ' ').toUpperCase()}</strong>
                  <p>Payroll, contractor onboarding, projects, tax documents, and approved payments use this separate worker permission.</p>
                  {portalIsUsable(vimPayWorkspace) ? <NavLink to="/pay">Open VIM Pay</NavLink> : <span>Contact VIM Media to restore worker access.</span>}
                </article>
              ) : null}
              {filmmakerWorkspace ? (
                <article className="account-card">
                  <h2>Filmmaker Distribution</h2>
                  <strong>{String(filmmakerWorkspace.status || 'active').replace(/_/g, ' ').toUpperCase()}</strong>
                  <p>Title delivery, distribution reports, filmmaker revenue, payouts, messages, and orders stay inside the Filmmaker workspace.</p>
                  {portalIsUsable(filmmakerWorkspace) ? <NavLink to="/filmmakers/dashboard">Open Filmmaker Portal</NavLink> : <span>Contact WatchVIM distribution support.</span>}
                </article>
              ) : null}
            </div>
          </section>
        ) : null}
        <button className="account-secondary" type="button" onClick={logout}>Log Out</button>
      </section>
    </RequireAccount>
  );
}

export function EditProfilePage() {
  const { user, session } = useSession();
  const [profile, setProfile] = useState(null);
  const [fullName, setFullName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarInputKey, setAvatarInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    loadProfile(user.id)
      .then((value) => {
        setProfile(value);
        setFullName(value?.full_name || user?.user_metadata?.full_name || '');
        setAvatarUrl(value?.avatar_url || avatarFromUser(user));
      })
      .catch((err) => setError(err?.message || 'Unable to load profile.'));
  }, [user?.id]);

  async function submit(event) {
    event.preventDefault(); setBusy(true); setError(''); setSuccess('');
    try {
      const value = await updateProfile(user.id, { full_name: fullName });
      setProfile((current) => ({ ...current, ...value, avatar_url: avatarUrl || current?.avatar_url || null }));
      setSuccess('Profile updated.');
    } catch (err) { setError(err?.message || 'Unable to update profile.'); }
    finally { setBusy(false); }
  }

  async function uploadAvatar() {
    if (!avatarFile || avatarBusy) return;
    setAvatarBusy(true); setError(''); setSuccess('');
    try {
      const uploadedUrl = await uploadProfileAvatar(session, avatarFile);
      setAvatarUrl(uploadedUrl);
      setProfile((current) => ({ ...current, avatar_url: uploadedUrl }));
      setAvatarFile(null);
      setAvatarInputKey((value) => value + 1);
      setSuccess('Profile photo updated.');
    } catch (err) { setError(err?.message || 'Unable to upload profile photo.'); }
    finally { setAvatarBusy(false); }
  }

  async function removeAvatar() {
    if (!avatarUrl || avatarBusy) return;
    setAvatarBusy(true); setError(''); setSuccess('');
    try {
      await removeProfileAvatar(session);
      setAvatarUrl('');
      setAvatarFile(null);
      setAvatarInputKey((value) => value + 1);
      setProfile((current) => ({ ...current, avatar_url: null }));
      setSuccess('Profile photo removed.');
    } catch (err) { setError(err?.message || 'Unable to remove profile photo.'); }
    finally { setAvatarBusy(false); }
  }

  const displayName = fullName || profile?.full_name || user?.email || 'WatchVIM Member';

  return (
    <RequireAccount>
      <AuthShell eyebrow="Profile" title="Edit profile" subtitle={profile?.email || user?.email} backTo="/profile" backLabel="Back to Profile">
        <div className="profile-photo-editor">
          <ProfileAvatar src={avatarUrl} name={displayName} email={user?.email} size="editor" />
          <div className="profile-photo-controls">
            <label className="profile-file-picker">
              <span>Choose profile photo</span>
              <input
                key={avatarInputKey}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                onChange={(event) => setAvatarFile(event.target.files?.[0] || null)}
                disabled={avatarBusy}
              />
            </label>
            <p className="account-muted profile-photo-help">JPG, PNG, WebP, GIF, or AVIF · maximum 5MB.</p>
            {avatarFile ? <p className="profile-selected-file">Selected: {avatarFile.name}</p> : null}
            <div className="profile-photo-actions">
              <button className="account-primary" type="button" disabled={!avatarFile || avatarBusy} onClick={uploadAvatar}>{avatarBusy && avatarFile ? 'Uploading…' : 'Upload Photo'}</button>
              {avatarUrl ? <button className="account-secondary compact" type="button" disabled={avatarBusy} onClick={removeAvatar}>Remove Photo</button> : null}
            </div>
          </div>
        </div>
        <form className="account-form" onSubmit={submit}>
          <Field label="Display name" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          <FormMessage error={error} success={success} />
          <button className="account-primary" type="submit" disabled={busy || avatarBusy}>{busy ? 'Saving…' : 'Save Profile'}</button>
        </form>
      </AuthShell>
    </RequireAccount>
  );
}

export function MembershipPage() {
  const { user, session } = useSession();
  const location = useLocation();
  const access = useAccess();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const profileContext = location.pathname.startsWith('/profile/');

  const subscription = access.subscription;
  const renewalCancelled = subscription.cancelAtPeriodEnd || ['CANCELLED', 'CANCELED'].includes(String(subscription.rawStatus || '').toUpperCase());
  const accessUntil = subscription.accessUntil ? new Date(subscription.accessUntil) : null;
  const accessUntilLabel = accessUntil && Number.isFinite(accessUntil.getTime())
    ? accessUntil.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  const canCancel = Boolean(user && session?.access_token && subscription.active && subscription.paypalSubscriptionId && !renewalCancelled);
  const showPlans = !user || (access.ready && !access.loading && !access.error && !subscription.active);

  async function submitCancellation() {
    if (!canCancel || cancelBusy) return;
    setCancelBusy(true); setError(''); setSuccess('');
    try {
      const result = await cancelMembership({ session, subscriptionId: subscription.paypalSubscriptionId, plan: subscription.plan });
      const resultDate = result?.access_until ? new Date(result.access_until) : null;
      const resultDateLabel = resultDate && Number.isFinite(resultDate.getTime())
        ? resultDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
        : '';
      await access.refresh();
      setConfirmCancel(false);
      setSuccess(resultDateLabel
        ? `Membership renewal canceled. You can keep watching through ${resultDateLabel}.`
        : 'Membership renewal canceled. No additional recurring membership charges will be made.');
    } catch (err) {
      setError(err?.message || 'Unable to cancel membership renewal. Please try again or contact WatchVIM Support.');
    } finally { setCancelBusy(false); }
  }

  return (
    <section className="account-page membership-page">
      {profileContext ? <ProfileBackLink /> : null}
      <div className="account-heading"><span className="eyebrow">WatchVIM Membership</span><h1>Stream WatchVIM your way.</h1><p>Membership unlocks SVOD titles and is the ad-free access tier across eligible WatchVIM streaming.</p></div>
      <FormMessage error={error || (access.error && user ? 'We could not verify your current membership status.' : '')} success={success} />
      {user && access.loading ? <p className="account-muted" role="status">Checking your membership…</p> : null}
      {user && !access.loading && subscription.active ? (
        <div className="membership-active membership-management">
          <div className="membership-management-copy">
            <strong>{renewalCancelled ? 'Renewal canceled' : 'Active membership'}</strong>
            <span>{subscription.plan || 'WatchVIM SVOD'}{accessUntilLabel ? ` · ${renewalCancelled ? 'access through' : 'current period through'} ${accessUntilLabel}` : ''}</span>
            <p>{renewalCancelled ? 'Automatic renewal is off. You will not be charged again for this membership.' : 'Automatic renewal is on. You can cancel future billing here at any time.'}</p>
          </div>
          {canCancel ? (
            <div className="membership-actions">
              {!confirmCancel ? (
                <button className="account-secondary" type="button" onClick={() => setConfirmCancel(true)}>Cancel Membership</button>
              ) : (
                <div className="membership-confirm" role="group" aria-label="Confirm membership cancellation">
                  <p>Cancel automatic renewal?{accessUntilLabel ? ` You will keep access through ${accessUntilLabel}.` : ' You will keep access through the end of the current paid period.'}</p>
                  <div>
                    <button className="account-danger" type="button" disabled={cancelBusy} onClick={submitCancellation}>{cancelBusy ? 'Canceling…' : 'Confirm Cancellation'}</button>
                    <button className="account-secondary" type="button" disabled={cancelBusy} onClick={() => setConfirmCancel(false)}>Keep Membership</button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
          {!renewalCancelled && subscription.active && !subscription.paypalSubscriptionId ? (
            <p className="membership-provider-note">This membership cannot be canceled through the website. Use the billing provider shown on your receipt or <NavLink to="/support">contact WatchVIM Support</NavLink>.</p>
          ) : null}
        </div>
      ) : null}
      {showPlans ? <MembershipPlans /> : null}
      <DeviceGuide />
    </section>
  );
}

export function RentalsPage() {
  const access = useAccess();
  return (
    <RequireAccount>
      <section className="account-page">
        <ProfileBackLink />
        <div className="account-heading"><span className="eyebrow">Account</span><h1>Your Rentals</h1><p>Server-verified WatchVIM rental access connected to this account.</p></div>
        {access.loading ? <p>Checking rentals…</p> : access.tvod.items.length ? <div className="rentals-list">{access.tvod.items.map((item) => <article className="account-card" key={item.id || `${item.title_id}-${item.paypal_order_id}`}><h2>{item.title_id}</h2><p>{item.purchase_type}</p><span>{item.access_until ? `Available through ${new Date(item.access_until).toLocaleString()}` : 'Active access'}</span><NavLink to={`/title/${encodeURIComponent(item.title_id)}`}>View Title</NavLink></article>)}</div> : <div className="account-card"><h2>No active rentals</h2><p>Titles you rent will appear here after PayPal and WatchVIM confirm the purchase.</p><NavLink to="/movies">Browse Movies</NavLink></div>}
      </section>
    </RequireAccount>
  );
}

export function BillingPage() {
  const access = useAccess();
  return (
    <RequireAccount>
      <section className="account-page">
        <ProfileBackLink />
        <div className="account-heading"><span className="eyebrow">Billing</span><h1>Membership &amp; purchases</h1></div>
        <div className="account-dashboard-grid">
          <article className="account-card"><h2>Streaming membership</h2><p>{access.subscription.active ? `Active · ${access.subscription.plan || 'WatchVIM'}` : 'No active membership'}</p><NavLink to="/profile/membership">Membership</NavLink></article>
          <article className="account-card"><h2>Rentals</h2><p>{access.tvod.items.length} active rental entitlement{access.tvod.items.length === 1 ? '' : 's'}.</p><NavLink to="/profile/rentals">Rentals</NavLink></article>
        </div>
      </section>
    </RequireAccount>
  );
}

